import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Check, AlertCircle, ArrowUpDown, FlaskConical, Archive } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

/**
 * Cross-link A/B experiments dashboard.
 *
 * Lists every experiment the partner has ever created (active + archived)
 * across all payment links, with live per-cohort stats. Sortable by
 * visitors, by absolute success-rate delta, by status, or by link.
 *
 * Data plumbing:
 *  1. `GET /partner/experiments` returns enriched items (link_code +
 *     link_display_name on each row) — single backend call.
 *  2. For each ACTIVE experiment we fan out `GET /partner/experiments/
 *     {id}/stats` in parallel to populate the live cohort cells.
 *     Archived rows skip stats fetch — their final numbers can still be
 *     reviewed from the Edit Payment Link modal.
 *  3. Both lists refresh every 20s while the page is open.
 *
 * The whole page is read-only EXCEPT the "Stop" action, which archives
 * an active experiment via DELETE (existing endpoint, soft-archives).
 */
export default function PartnerExperiments() {
    const [experiments, setExperiments] = useState([]);
    const [stats, setStats] = useState({});         // {experiment_id: stats}
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortKey, setSortKey] = useState("visitors");
    const [sortDir, setSortDir] = useState("desc");
    const [showArchived, setShowArchived] = useState(false);

    const refresh = async ({ initial = false } = {}) => {
        if (initial) setLoading(true);
        try {
            const r = await api.get(`/partner/experiments`);
            const items = r.data?.items || [];
            setExperiments(items);
            setError(null);
            // Fan-out fetch stats for active experiments only.
            const activeIds = items
                .filter((e) => e.status === "active")
                .map((e) => e.experiment_id);
            const results = await Promise.all(activeIds.map((id) =>
                api.get(`/partner/experiments/${id}/stats`)
                    .then((rr) => [id, rr.data])
                    .catch(() => [id, null])
            ));
            const map = {};
            for (const [id, s] of results) if (s) map[id] = s;
            setStats(map);
        } catch (e) {
            setError(formatDetail(e?.response?.data?.detail) || e.message);
        } finally {
            if (initial) setLoading(false);
        }
    };

    useEffect(() => {
        refresh({ initial: true });
        const t = setInterval(refresh, 20000);
        return () => clearInterval(t);
         
    }, []);

    const archive = async (expId) => {
        if (!window.confirm("Archive this experiment? Past assignments stay intact but no new visitors will be bucketed.")) return;
        try {
            await api.delete(`/partner/experiments/${expId}`);
            refresh();
        } catch (e) {
            alert(formatDetail(e?.response?.data?.detail) || e.message);
        }
    };

    // Helper: per-row computed metrics used for both rendering AND sorting.
    const enriched = useMemo(() => experiments.map((e) => {
        const s = stats[e.experiment_id] || {};
        const va = s.variants?.a || {};
        const vb = s.variants?.b || {};
        const total = s.total_visitors || 0;
        // Winner: variant with the higher success_rate, IF both have visitors.
        let winner = null;
        if (va.visitors && vb.visitors) {
            if ((va.success_rate || 0) > (vb.success_rate || 0)) winner = "a";
            else if ((vb.success_rate || 0) > (va.success_rate || 0)) winner = "b";
        }
        const delta = winner
            ? Math.abs((va.success_rate || 0) - (vb.success_rate || 0))
            : 0;
        return { ...e, _va: va, _vb: vb, _total: total, _winner: winner, _delta: delta };
    }), [experiments, stats]);

    // Apply visibility filter (status) then sort.
    const visibleRows = useMemo(() => {
        const rows = enriched.filter((r) =>
            showArchived ? true : r.status === "active"
        );
        const sorted = [...rows].sort((a, b) => {
            let av, bv;
            switch (sortKey) {
                case "visitors": av = a._total; bv = b._total; break;
                case "delta": av = a._delta; bv = b._delta; break;
                case "link": av = a.link_code || ""; bv = b.link_code || ""; break;
                case "status": av = a.status; bv = b.status; break;
                case "key": av = a.variant_key; bv = b.variant_key; break;
                default: av = a._total; bv = b._total;
            }
            if (av < bv) return sortDir === "asc" ? -1 : 1;
            if (av > bv) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [enriched, sortKey, sortDir, showArchived]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir(key === "link" || key === "status" || key === "key" ? "asc" : "desc");
        }
    };

    // Aggregate counters for the page header.
    const activeCount = enriched.filter((e) => e.status === "active").length;
    const archivedCount = enriched.length - activeCount;
    const totalVisitorsAcrossActive = enriched
        .filter((e) => e.status === "active")
        .reduce((acc, e) => acc + (e._total || 0), 0);

    return (
        <div className="space-y-5 max-w-6xl" data-testid="partner-experiments-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <FlaskConical className="w-6 h-6 text-emerald-600" />
                        A/B experiments
                    </h1>
                    <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                        All active and archived experiments across your payment links. Bucketing is
                        sticky-per-txn and deterministic. To spin up a new experiment, open the
                        target payment link and use the &quot;A/B experiments&quot; panel there.
                    </p>
                </div>
                <div className="flex flex-col items-end gap-1 text-right">
                    <div className="text-xs text-slate-500">
                        <span data-testid="experiments-active-count" className="font-semibold text-emerald-700">{activeCount}</span> active
                        {" · "}
                        <span className="font-semibold text-slate-700">{archivedCount}</span> archived
                    </div>
                    <div className="text-[11px] text-slate-500">
                        <span className="font-semibold text-slate-700">{totalVisitorsAcrossActive.toLocaleString("en-IN")}</span> visitors bucketed
                    </div>
                </div>
            </div>

            <div className="card p-0">
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-md">
                    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                            type="checkbox"
                            data-testid="experiments-show-archived"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                        />
                        Show archived
                    </label>
                    {loading && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                        </div>
                    )}
                </div>

                {error && (
                    <div className="px-4 py-3 text-xs text-rose-700 bg-rose-50 border-b border-rose-200 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {!loading && visibleRows.length === 0 && (
                    <div className="px-4 py-12 text-center text-sm text-slate-500">
                        {experiments.length === 0
                            ? "No experiments yet. Open any payment link → A/B experiments → New experiment to start one."
                            : "No experiments match the current filter. Toggle 'Show archived' to see past tests."}
                    </div>
                )}

                {visibleRows.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="experiments-table">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50/50">
                                    <SortableHead label="Link" k="link" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                                    <SortableHead label="Variant" k="key" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                                    <th className="px-3 py-2 font-semibold">A / B</th>
                                    <SortableHead label="Visitors" k="visitors" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                                    <th className="px-3 py-2 font-semibold">Cohort A</th>
                                    <th className="px-3 py-2 font-semibold">Cohort B</th>
                                    <SortableHead label="Δ Success" k="delta" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                                    <SortableHead label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                                    <th className="px-3 py-2 font-semibold"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map((row) => (
                                    <ExperimentRow key={row.experiment_id} row={row} onArchive={archive} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function SortableHead({ label, k, sortKey, sortDir, onClick, align = "left" }) {
    const active = sortKey === k;
    return (
        <th className={`px-3 py-2 font-semibold ${align === "right" ? "text-right" : ""}`}>
            <button
                type="button"
                onClick={() => onClick(k)}
                data-testid={`exp-sort-${k}`}
                className={`inline-flex items-center gap-1 hover:text-slate-700 ${active ? "text-slate-900" : ""}`}
            >
                {label}
                <ArrowUpDown className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`} />
                {active && <span className="text-[9px] text-slate-500">{sortDir === "asc" ? "↑" : "↓"}</span>}
            </button>
        </th>
    );
}

function ExperimentRow({ row, onArchive }) {
    const { _va: va, _vb: vb, _total: total, _winner: winner, _delta: delta } = row;
    const isActive = row.status === "active";
    const enoughSample = total >= 60;  // 30 per cohort
    const significantDelta = delta >= 5; // ≥5 pp absolute spread feels meaningful
    const callout = isActive && enoughSample && significantDelta && winner;
    return (
        <tr data-testid={`exp-row-${row.experiment_id}`}
            className={`border-b border-slate-100 hover:bg-slate-50 ${row.status === "archived" ? "opacity-60" : ""}`}>
            <td className="px-3 py-2.5 align-top min-w-[140px]">
                <Link
                    to={`/partner/links`}
                    className="font-medium text-slate-900 hover:text-blue-600 truncate inline-block max-w-[160px]"
                    title={row.link_display_name}
                >
                    {row.link_display_name}
                </Link>
                <div className="text-[10px] text-slate-500 font-mono truncate max-w-[160px]" title={row.link_code}>{row.link_code}</div>
            </td>
            <td className="px-3 py-2.5 align-top">
                <div className="font-mono text-[11px] text-slate-700">{row.variant_key}</div>
                <div className="text-[10px] text-slate-500 truncate max-w-[180px]" title={row.label}>{row.label}</div>
            </td>
            <td className="px-3 py-2.5 align-top text-[11px] font-mono">
                <div>A = <span className="text-slate-900">{String(row.treatment_a_value)}</span></div>
                <div>B = <span className="text-slate-900">{String(row.treatment_b_value)}</span></div>
            </td>
            <td className="px-3 py-2.5 align-top text-right font-semibold">
                {isActive ? total.toLocaleString("en-IN") : <span className="text-slate-400">—</span>}
            </td>
            <CohortCell variant={va} isWinner={winner === "a"} isActive={isActive} />
            <CohortCell variant={vb} isWinner={winner === "b"} isActive={isActive} />
            <td className="px-3 py-2.5 align-top text-right">
                {isActive && winner ? (
                    <div>
                        <div className={`text-xs font-semibold ${callout ? "text-emerald-700" : "text-slate-700"}`}>
                            +{delta.toFixed(1)}%
                        </div>
                        {callout && <div className="text-[9px] uppercase text-emerald-700 font-bold tracking-wider">Significant</div>}
                        {isActive && !callout && total < 60 && <div className="text-[9px] text-amber-700">small sample</div>}
                    </div>
                ) : isActive ? (
                    <div className="text-[10px] text-slate-400">waiting</div>
                ) : (
                    <div className="text-slate-400">—</div>
                )}
            </td>
            <td className="px-3 py-2.5 align-top">
                {isActive ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                        <Check className="w-3 h-3" /> Active
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        <Archive className="w-3 h-3" /> Archived
                    </span>
                )}
            </td>
            <td className="px-3 py-2.5 align-top text-right">
                {isActive && (
                    <button
                        type="button"
                        data-testid={`exp-row-stop-${row.experiment_id}`}
                        onClick={() => onArchive(row.experiment_id)}
                        className="text-[10px] text-rose-600 hover:bg-rose-50 px-1.5 py-0.5 rounded"
                    >
                        Stop
                    </button>
                )}
            </td>
        </tr>
    );
}

function CohortCell({ variant, isWinner, isActive }) {
    if (!isActive) {
        return <td className="px-3 py-2.5 align-top text-slate-400">—</td>;
    }
    return (
        <td className={`px-3 py-2.5 align-top ${isWinner ? "bg-emerald-50/60" : ""}`}>
            <div className="text-[11px]">
                <span className="text-slate-500">v:</span>{" "}
                <span className="font-semibold">{(variant.visitors || 0)}</span>
            </div>
            <div className="text-[11px]">
                <span className="text-slate-500">utr:</span>{" "}
                <span className="font-semibold">{(variant.utr_rate || 0)}%</span>
            </div>
            <div className="text-[11px]">
                <span className="text-slate-500">win:</span>{" "}
                <span className={`font-semibold ${isWinner ? "text-emerald-700" : ""}`}>{(variant.success_rate || 0)}%</span>
            </div>
        </td>
    );
}
